/**
 * CNC Cart Bridge — Dawn Edition
 * ================================
 * Replaces Stiletto's theme.js cart operations for the CNC slide cart.
 *
 * Handles:
 *   1. Drawer open/close (overlay, icon, escape)
 *   2. Cart item quantity change (data-add-quantity, data-subtract-quantity)
 *   3. Cart item remove (data-remove-item)
 *   4. Cross-sell quick add (data-cnc-quick-add)
 *   5. ATC redirect — Dawn pub/sub → open drawer
 *   6. Section refresh after every cart change
 *
 * On Stiletto, theme.js handled #2-#4. Dawn has no equivalent.
 * This file IS the cart operations layer for Dawn.
 */
(function () {
  'use strict';

  var QUICK_CART_SELECTOR = '[data-section-type="quick-cart"]';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ============================================================
     SECTION ID — resolve once, reuse
     ============================================================ */
  function getSectionId() {
    var sectionEl = document.querySelector(QUICK_CART_SELECTOR);
    if (!sectionEl) return 'quick-cart';
    var container = sectionEl.closest('[id^="shopify-section"]');
    return container ? container.id.replace('shopify-section-', '') : 'quick-cart';
  }

  /* ============================================================
     DRAWER OPEN / CLOSE
     ============================================================ */
  /* THE OPEN FLAG LIVES ON <body>, NOT ON THE WRAPPER.
     refreshCartSection() innerHTML-replaces the whole quick-cart section, which
     destroys .quick-cart__wrapper and every class on it — including `active`.
     <body> survives that, so `quick-cart-open` is the only state that can be
     trusted across a refresh. Read it at APPLY time, never capture it before a
     fetch. (Tracy 2026-09-25: "presses twice... that's the bit that infuriates
     me." This is why.) */
  function isDrawerOpen() {
    return document.body.classList.contains('quick-cart-open');
  }

  /* ============================================================
     AUTO-DISMISS AFTER AN ADD
     Tracy 2026-09-25: "if someone picks up a shirt and keeps walking — great, I
     want this. But if you just stand there in front of them, you're blocking them
     from continuing to shop... I think we're pissing off more people by not auto
     closing the drawer."

     Measured, 24 Sep UTC: of five mobile sessions that added to bag, NONE reached
     checkout. What they did instead was tap the drawer header four to six times
     trying to dismiss it, finally hit the ×, and go straight back to browsing.
     The drawer was the obstacle, not the offer.

     So: an add opens the drawer, it confirms what went in and what the total is,
     and then it gets out of the way. Opening it DELIBERATELY (the bag icon) is a
     different intent entirely and never auto-closes. Any touch inside cancels the
     dismissal, so nobody loses the drawer mid-read.

     Tracy accepted the trade this makes — the cart cross-sell gets less airtime —
     on the view that the PDP gallery is where that discovery should happen. */
  /* Three seconds, measured from the TAP. Tracy asked for two, tested it live and
     came back: "the slider actually doesn't have enough time to visually add it to
     the bag before the slider disappears — maybe go back to three." The drawer has
     to render the new line before it leaves, or the confirmation confirms nothing.
     Back to 3s pending her next live test."

     The countdown is anchored to the tap because openDrawer() now runs
     synchronously as the /cart/add request leaves, before the network. And it is
     deliberately NOT restarted partway through: a single add calls openDrawer()
     more than once (the fetch interceptor as the request goes out, then Dawn's
     cart-update once it comes back), and re-arming on the second call is what
     stretched a 3s timer into ~4s on staging. One add, one deadline. */
  var AUTO_DISMISS_MS = 3000;
  var dismissTimer = null;
  var autoEpisode = false;   /* true while one add's countdown is running */

  /* THE COUNTDOWN MUST START WHEN THE ITEM IS ON SCREEN, NOT WHEN THE FRAME OPENS.
     Tracy 2026-09-27: "it all loads at the same time as its closing."

     The drawer opens before the network (deliberately — see openDrawer), so the
     three seconds used to begin while it still showed the OLD cart. Measured cold:
     open at 0ms, add resolves ~400ms, section HTML lands ~1060ms, dismiss at
     3000ms — the customer's own item was visible for under two of the three
     seconds it was promised, and on a slower connection it arrives as the drawer
     is already leaving.

     So an add-initiated open now ARMS the countdown and refreshCartSection starts
     it once there is something to read. */
  var dismissArmed = false;

  function cancelAutoDismiss() {
    clearTimeout(dismissTimer);
    dismissTimer = null;
    autoEpisode = false;
    dismissArmed = false;
  }



  /* Called when the refreshed contents are in place — on success OR failure, so a
     dropped request can never leave the drawer parked open forever. */
  function startArmedDismiss() {
    if (!dismissArmed) return;
    dismissArmed = false;
    if (isDrawerOpen()) scheduleAutoDismiss();
  }

  function scheduleAutoDismiss() {
    /* Already counting down for this add — leave the original deadline alone. */
    if (autoEpisode && dismissTimer) return;
    autoEpisode = true;
    clearTimeout(dismissTimer);
    dismissTimer = setTimeout(function () {
      dismissTimer = null;
      autoEpisode = false;
      /* Re-check: a manual open or a close may have happened since. */
      if (isDrawerOpen()) closeDrawer();
    }, AUTO_DISMISS_MS);
  }

  /* Any deliberate touch inside the drawer means "I am reading this" — keep it. */
  ['click', 'touchstart', 'wheel'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      if (!dismissTimer) return;
      if (e.target && e.target.closest && e.target.closest('.quick-cart__wrapper')) cancelAutoDismiss();
    }, { passive: true, capture: true });
  });

  function openDrawer(opts) {
    var quickCart = document.querySelector(QUICK_CART_SELECTOR);
    if (!quickCart) return;
    /* wrapper is a CHILD of quickCart, not a parent — use querySelector not closest */
    var wrapper = quickCart.querySelector('.quick-cart__wrapper');
    if (!wrapper) return;

    /* Idempotent: always re-assert both flags. A refresh landing mid-open can
       rebuild the wrapper without `active` while <body> still says open, so
       re-asserting is what heals it — the old early-return left it half-open. */
    wrapper.classList.add('active');
    wrapper.setAttribute('aria-hidden', 'false');
    document.body.classList.add('quick-cart-open');

    /* ALWAYS refresh, even when already open. The old code returned early if the
       wrapper already had `active`, which meant a SECOND add while the drawer was
       open never re-fetched the section — the item went into the cart but the
       drawer kept showing the old contents. That alone is worth a second press.
       Refreshing unconditionally is safe now because refreshCartSection()
       coalesces: one in flight, at most one queued, newest response wins.

       EXCEPT on the optimistic open that happens as /cart/add leaves. Tracy
       2026-09-25: "when the product is adding to cart it takes too long — of that
       three seconds it probably spent two of those seconds just trying to update."
       Measured: drawer open at 81ms, but the line only visible at 1612ms, so the
       item was on screen for 1446ms of the 3058ms the drawer was up.

       The reason is that the drawer opened BEFORE the add had reached the server,
       and immediately asked for the cart — which of course came back without the
       new item. The line only appeared on the SECOND refresh, after the add
       resolved. One whole round trip spent fetching a cart we already knew was
       out of date. So the pre-request open skips it; the post-response handler
       does the one refresh that can actually contain the item. */
    if (!(opts && opts.skipRefresh)) refreshCartSection();

    /* Opened by an add -> confirm and get out of the way. Opened by the bag icon
       -> the shopper asked for it; leave it until they close it.

       On the pre-request open there is nothing to read yet, so ARM the countdown
       and let refreshCartSection start it when the contents land. Any other auto
       open (the drawer was already current) starts it straight away. */
    if (opts && opts.auto) {
      if (opts.skipRefresh) { dismissArmed = true; }
      else scheduleAutoDismiss();
    } else cancelAutoDismiss();
  }

  function closeDrawer() {
    cancelAutoDismiss();
    var wrapper = document.querySelector('.quick-cart__wrapper.active');
    if (wrapper) {
      wrapper.classList.remove('active');
      wrapper.setAttribute('aria-hidden', 'true');
    }
    /* Always release the body scroll-lock — the CNC drawer IS the cart UX, so
       closing it must restore page scroll. Dawn's native cart-drawer.js ALSO adds
       'overflow-hidden' to <body> on add-to-cart (even though that drawer is
       display:none here), and the old close only removed 'quick-cart-open' — leaving
       <body> locked, so the PDP couldn't scroll after closing the cart (Tracy
       2026-06-15, live). Clear both, and run regardless of the wrapper state. */
    document.body.classList.remove('quick-cart-open', 'overflow-hidden');
  }

  /* ============================================================
     SECTION REFRESH — fetch fresh HTML from server
     ============================================================ */
  /* ONE REFRESH AT A TIME, AND THE LAST WORD WINS.
     Adding to cart used to start up to four of these at once — addToCart() called
     one directly, openDrawer() called another, the `cart-update` subscriber fired
     at +50ms and the window.fetch intercept at +100ms. Each is an independent
     fetch that innerHTML-replaces the drawer whenever it happens to land, and each
     had captured its own stale `wasOpen`. Whichever resolved LAST won — so if the
     one captured before the drawer opened came back last, it rebuilt the drawer
     without `active` and the drawer silently shut itself. Intermittent by nature,
     which is exactly how it presented: sometimes Add to Bag worked, sometimes it
     looked like nothing happened and people pressed again.
     Fix: coalesce (one in flight, at most one queued) and stamp a generation so a
     superseded response is discarded instead of painting over a newer one. */
  var refreshGen = 0;
  var refreshInFlight = false;
  var refreshQueued = false;

  /* Move <img> nodes that have already decoded from the LIVE drawer into the FRESH
     tree, matched on src. Called just before the fresh nodes are moved into place,
     so an unchanged line keeps the exact element that is already painted.

     Only images that are genuinely ready are carried over — `complete` alone can be
     true for a broken image, so naturalWidth must be non-zero too. Anything not
     carried over is simply left as the fresh node and loads the normal way. */
  function carryOverDecodedImages(liveRoot, freshRoot) {
    try {
      var live = liveRoot.querySelectorAll('img[src]');
      if (!live.length) return;

      var bySrc = {};
      for (var i = 0; i < live.length; i++) {
        var img = live[i];
        if (img.complete && img.naturalWidth > 0 && !bySrc[img.src]) bySrc[img.src] = img;
      }

      var fresh = freshRoot.querySelectorAll('img[src]');
      for (var j = 0; j < fresh.length; j++) {
        var f = fresh[j];
        /* f.src resolves relative URLs the same way the live one did, so the two
           are comparable without normalising by hand. */
        var keep = bySrc[f.src];
        if (!keep || !f.parentNode) continue;
        delete bySrc[f.src];              /* one live node can only be moved once */
        /* Carry the fresh attributes onto the decoded node so a changed class,
           size or alt still applies — we are preserving PIXELS, not markup. */
        for (var a = 0; a < f.attributes.length; a++) {
          var at = f.attributes[a];
          if (at.name !== 'src') keep.setAttribute(at.name, at.value);
        }
        f.parentNode.replaceChild(keep, f);
      }
    } catch (e) {
      /* Never let a paint optimisation stop the cart from updating. */
    }
  }

  /* KEEP THE CHECKOUT BUTTON'S NODE ALIVE ACROSS A REFRESH.

     Tracy 2026-09-28, twice in one session and once in a clean incognito window:
     "it took me pressing go to checkout twice before I actually made it to
     checkout." Also the night before: "it took me two presses on checkout."

     This is a DOM-identity bug, not a slow request. A tap only becomes a `click`
     if the press and the release land on the SAME node — so if that node leaves the
     document in between, the browser fires nothing at all and the tap is silently
     discarded. `replaceChildren` below rebuilds the entire drawer subtree, the
     CHECKOUT button with it, and opening the cart from the bag icon starts a
     `cart?section_id=` fetch that lands 330ms (warm) to 660ms (cold) later —
     exactly the window in which a thumb is arriving at the button. Nothing appears
     to happen, so of course you press again, and the second press works because by
     then the tree is settled.

     Note this is the same failure shape as the one the coalescing comment above
     describes ("sometimes it looked like nothing happened and people pressed
     again"). That fix stopped four refreshes racing; it did not stop the surviving
     refresh from destroying the button under the customer's finger.

     So: move the LIVE button node into the fresh tree instead of letting the fresh
     one replace it. Moving a node does not destroy it — same element, same
     in-flight gesture, the click still fires — and it lands inside the fresh <form>
     so `name="checkout"` still submits to Shopify's cart handler exactly as before.
     The server's label and total are copied onto it, so the price shown is still the
     server's answer and never a stale optimistic one.

     Deliberately NARROW: only this one button. The close button holds an <svg>, so
     its touch target is a child node that innerHTML would destroy anyway, and the
     quantity controls already answer instantly from applyQtyLocally. If the button
     is missing on either side this does nothing and the refresh behaves as it did.

     This is on the money path, so it may not throw: the whole thing is wrapped, and
     a failure leaves the ordinary replaceChildren behaviour untouched. */
  function carryOverCheckoutButton(liveRoot, freshRoot) {
    try {
      var live = liveRoot.querySelector('[data-checkout-btn]');
      var fresh = freshRoot.querySelector('[data-checkout-btn]');
      if (!live || !fresh || live === fresh || !fresh.parentNode) return;

      /* The server is the truth for what the button SAYS; the live node is the
         truth for the gesture already underway. Take both. */
      for (var a = 0; a < fresh.attributes.length; a++) {
        var at = fresh.attributes[a];
        live.setAttribute(at.name, at.value);
      }
      live.textContent = fresh.textContent;

      fresh.parentNode.replaceChild(live, fresh);
    } catch (e) {
      /* A lost tap is better than a cart that cannot update. */
    }
  }

  function refreshCartSection() {
    if (refreshInFlight) { refreshQueued = true; return; }
    refreshInFlight = true;
    var myGen = ++refreshGen;
    var sectionId = getSectionId();

    fetch(ROOT + 'cart?section_id=' + sectionId)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        /* A newer refresh has started since this one — drop this response. */
        if (myGen !== refreshGen) return;

        var temp = document.createElement('div');
        temp.innerHTML = html;
        var freshContent = temp.querySelector(QUICK_CART_SELECTOR);
        var currentEl = document.querySelector(QUICK_CART_SELECTOR);
        if (freshContent && currentEl) {
          /* KEEP THE IMAGES THAT ARE ALREADY ON SCREEN.
             Tracy 2026-09-27: "the cart has like... flickers of the images. I just
             removed my cart and it is like it flickers or glitches as it's changing."

             Assigning innerHTML serialises the fresh tree to a STRING and re-parses
             it, so every <img> in the drawer is destroyed and rebuilt — including the
             ones for lines that did not change. A rebuilt <img> has to be decoded
             again before it paints, so the untouched rows blink while the server
             response is applied. That is the flicker, and it is worse the more items
             are in the cart.

             So: carry the already-decoded <img> nodes across by src, then move the
             fresh NODES in rather than re-parsing a string. An image that is already
             complete keeps its pixels and never repaints; anything genuinely new
             still loads normally. */
          carryOverDecodedImages(currentEl, freshContent);
          carryOverCheckoutButton(currentEl, freshContent);

          if (currentEl.replaceChildren) {
            currentEl.replaceChildren.apply(
              currentEl, Array.prototype.slice.call(freshContent.childNodes)
            );
          } else {
            /* Old Safari: no replaceChildren. Fall back to the original behaviour —
               it flickers, but it is correct, and correctness is not optional here. */
            currentEl.innerHTML = freshContent.innerHTML;
          }
        }
        /* Read the intended state NOW, from <body> — not from a flag captured
           before the fetch, and not from the wrapper we just destroyed. */
        if (isDrawerOpen()) {
          var wrapper = document.querySelector('.quick-cart__wrapper');
          if (wrapper) {
            wrapper.classList.add('active');
            wrapper.setAttribute('aria-hidden', 'false');
          }
        }
        /* Update Dawn's cart count bubble */
        updateCartCount();
      })
      .catch(function () {})
      .then(function () {
        refreshInFlight = false;
        /* Contents are now as good as they are going to get — success or not.
           Start any armed countdown here rather than at open, so the three
           seconds are three seconds of the customer seeing their own item. */
        startArmedDismiss();
        if (refreshQueued) { refreshQueued = false; refreshCartSection(); }
      });
  }

  /* Update the cart count in the header */
  function updateCartCount() {
    fetch(ROOT + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* EVERY bag, and the right span inside each one.
           There are two bags in this header — #cart-icon-bubble (desktop, inside
           .cnc-header__lower, display:none on a phone) and #cart-icon-bubble-mobile — so
           querySelector reached only the first and left the visible one untouched.
           And Dawn's bubble holds TWO spans: the number, and a screen-reader label. Writing
           the count into `.cart-count-bubble span` wrote it into both, so the bag's text read
           "Cart11" for a single item and a screen reader announced "1" instead of "1 item". */
        document.querySelectorAll('.cart-count-bubble').forEach(function (bubble) {
          var visible = bubble.querySelector('span[aria-hidden="true"]') || bubble.querySelector('span:not(.visually-hidden)');
          if (visible) visible.textContent = cart.item_count < 100 ? cart.item_count : '99+';
          var label = bubble.querySelector('span.visually-hidden');
          if (label) label.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
          bubble.style.display = cart.item_count > 0 ? '' : 'none';
        });
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        /* The bag ICON itself changes with state in Liquid (icon-cart-empty vs icon-cart) and
           is never re-rendered on an AJAX add, so the empty bag would keep its empty glyph
           while showing a count beside it. */
        document.querySelectorAll('#cart-icon-bubble, #cart-icon-bubble-mobile').forEach(function (bag) {
          var svg = bag.querySelector('svg');
          if (svg && cart.item_count > 0) svg.classList.replace('icon-cart-empty', 'icon-cart');
        });
      })
      .catch(function () {});
  }

  /* ============================================================
     CART OPERATIONS — the AJAX layer Stiletto's theme.js provided
     ============================================================ */

  /* Change quantity of a line item by key.

     INSTANT, THEN RECONCILE. Tracy 2026-09-25, on removing a line: "it took me
     three presses and it was really slow, it wasn't instant."

     It was doing a full server round trip AND a whole-section re-render before
     anything visibly changed — the only immediate feedback was the row fading to
     15% opacity, which reads as "stuck" rather than "gone". So each press felt
     ignored, and each press queued another round trip.

     Now: the DOM changes on the tap, and the server is told once the tapping
     stops. Three quick taps on minus are one request for the final quantity
     instead of three requests racing each other. */
  var pendingQty = {};      /* key -> the quantity we intend the server to end on */
  var qtyTimers = {};

  function changeQuantity(key, quantity) {
    pendingQty[key] = quantity;
    clearTimeout(qtyTimers[key]);

    /* A REMOVE HAS NOTHING TO COALESCE, SO IT SHOULD NOT WAIT.
       Tracy 2026-09-27: "removing items takes too long."

       The 300ms debounce exists because tapping + or - repeatedly should produce
       one request instead of six. Removing a line is a single deliberate act on a
       row that is about to disappear — there is no second tap coming, so the delay
       is pure latency on top of the POST (~400ms) and the section refresh (~660ms
       cold). Firing immediately takes roughly a fifth off the wait for the one
       cart action that most needs to feel decisive.

       Quantity changes keep the debounce exactly as it was. */
    var delay = quantity <= 0 ? 0 : 300;

    qtyTimers[key] = setTimeout(function () {
      var finalQty = pendingQty[key];
      delete pendingQty[key];
      delete qtyTimers[key];
      fetch(ROOT + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: finalQty })
      })
      .then(function () {
        refreshCartSection();
        /* Publish Dawn event so other components update. Tagged 'cnc-drawer' so this
           file's own cart-update subscriber knows it is a +/−/remove made in the open
           drawer, not an add, and does not start the add's auto-dismiss (2026-10-03:
           untagged, it shut the drawer ~4s after every + tap). Not 'cart-items': Dawn's
           cart page skips its re-render on that source, and it should still re-render. */
        if (typeof publish === 'function') publish('cart-update', { source: 'cnc-drawer' });
      })
      .catch(function () { refreshCartSection(); });
    }, delay);
  }

  /* Reflect the change in the DOM straight away so the tap feels answered. The
     server call above is what makes it true; this is what makes it feel instant. */
  function applyQtyLocally(item, newQty) {
    if (!item) return;
    if (newQty <= 0) {
      item.style.transition = 'opacity 120ms ease';
      item.style.opacity = '0';
      /* Take it out of the flow immediately — a faded-but-present row is exactly
         what made this feel broken. */
      item.style.display = 'none';
      return;
    }
    var input = item.querySelector('[data-quantity-input]');
    /* Writing the input is what lets a rapid second tap compound correctly:
       the next click reads this value rather than the stale server one. */
    if (input) input.value = newQty;
  }

  /* Add item to cart */
  function addToCart(variantId, quantity) {
    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: quantity || 1 }] })
    })
    .then(function () {
      /* openDrawer() does the refresh itself. Calling refreshCartSection() here
         as well was the second of the racing fetches — it captured the drawer as
         closed, then landed after the open and wiped it. */
      openDrawer({ auto: true });
      if (typeof publish === 'function') publish('cart-update', {});
    })
    .catch(function () { refreshCartSection(); });
  }

  /* ============================================================
     EVENT HANDLERS — bind to CNC cart button data attributes
     ============================================================ */
  function bindCartOperations() {
    document.addEventListener('click', function (e) {

      /* --- Quantity add --- */
      var addBtn = e.target.closest('[data-add-quantity]');
      if (addBtn) {
        e.preventDefault();
        e.stopPropagation();
        var item = addBtn.closest('[data-input-item]');
        if (!item) return;
        var key = item.getAttribute('data-key');
        var input = item.querySelector('[data-quantity-input]');
        if (key && input) {
          var newQty = parseInt(input.value, 10) + 1;
          applyQtyLocally(item, newQty);
          changeQuantity(key, newQty);
        }
        return;
      }

      /* --- Quantity subtract --- */
      var subBtn = e.target.closest('[data-subtract-quantity]');
      if (subBtn) {
        e.preventDefault();
        e.stopPropagation();
        var item = subBtn.closest('[data-input-item]');
        if (!item) return;
        var key = item.getAttribute('data-key');
        var input = item.querySelector('[data-quantity-input]');
        if (key && input) {
          var currentQty = parseInt(input.value, 10);
          if (currentQty <= 1) {
            applyQtyLocally(item, 0);   /* the trash state of the minus button */
            changeQuantity(key, 0);
          } else {
            applyQtyLocally(item, currentQty - 1);
            changeQuantity(key, currentQty - 1);
          }
        }
        return;
      }

      /* --- Remove item --- */
      var removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        var item = removeBtn.closest('[data-input-item]');
        if (!item) return;
        var key = item.getAttribute('data-key');
        if (key) {
          applyQtyLocally(item, 0);
          changeQuantity(key, 0);
        }
        return;
      }

      /* --- Cross-sell / upsell quick add --- */
      var quickAddBtn = e.target.closest('[data-cnc-quick-add]');
      if (quickAddBtn) {
        e.preventDefault();
        var variantId = quickAddBtn.getAttribute('data-cnc-quick-add');
        /* Check if there's a variant selector nearby */
        var card = quickAddBtn.closest('.cnc-cross-sell__card, .cnc-upsell-item, .cnc-threshold');
        if (card) {
          var select = card.querySelector('[data-variant-selector], .cnc-cross-sell__size-select, .cnc-upsell-item__variant-select');
          if (select) variantId = select.value;
        }
        if (variantId) {
          quickAddBtn.disabled = true;
          quickAddBtn.textContent = 'ADDING...';
          addToCart(variantId, 1);
        }
        return;
      }

      /* --- Overlay click → close --- */
      if (e.target.classList.contains('quick-cart__overlay')) {
        closeDrawer();
        return;
      }

      /* --- Close button --- */
      /* THE HEADING IS A DISMISSAL TARGET, because that is what people use it as.
         "My Bag" is an <h2> with no behaviour, sitting beside the ×. Tracy spotted the
         pattern on 2026-09-25 — sessions tapping the drawer header four to six times, the
         taps stopping the moment someone finally hit the × — and it is still happening:
         2026-09-30, a mobile session recorded `Dead click "My Bag"` immediately followed by
         a second tap on the same heading. A tap there is never "I want to read this
         heading"; it is "let me out". So it lets them out. */
      var closeBtn = e.target.closest('.quick-cart__close, .quick-cart__close-icon, [data-quick-cart-close], [data-modal-close], .quick-cart__heading');
      if (closeBtn) {
        e.preventDefault();
        closeDrawer();
        return;
      }

      /* --- Cart icon → open drawer --- */
      var cartLink = e.target.closest('a[href="/cart"], a[href$="/cart"], .header__icon--cart, #cart-icon-bubble');
      if (cartLink) {
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
        return;
      }
    });

    /* Escape key */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDrawer();
    });
  }

  /* ============================================================
     DAWN ATC REDIRECT — open CNC drawer after Add to Cart
     ============================================================ */
  function suppressDawnNotification() {
    var notification = document.querySelector('cart-notification');
    if (notification) {
      notification.style.display = 'none';
      notification.classList.remove('active');
      notification.removeAttribute('open');
    }
  }

  function bindDawnATC() {
    /* Dawn pub/sub */
    if (typeof subscribe === 'function') {
      subscribe('cart-update', function (event) {
        suppressDawnNotification();
        /* A change made INSIDE the open drawer (+, −, remove — Dawn's cart.js publishes
           these as source 'cart-items') is not an add. Treating it as one started the 3s
           auto-dismiss, so the drawer shut ~3.8s after the shopper tapped + (measured
           2026-10-03 on live), even when they had opened it with the bag icon, which by
           the rule above never auto-closes. Refresh it, cancel any countdown, keep it open. */
        if (event && (event.source === 'cart-items' || event.source === 'cnc-drawer') && isDrawerOpen()) {
          openDrawer();
          return;
        }
        openDrawer({ auto: true });
      });
    }

    /* Fetch intercept for /cart/add — catches all ATC including apps.

       OPEN ON THE REQUEST, NOT THE RESPONSE. Tracy 2026-09-25: "when you add to
       bag it's only short, but it's not instant... it's maybe half a second if
       not a little bit less, but it feels like it's long enough to not be
       working."

       She is right, and the wait was mostly self-inflicted. This used to wait for
       the /cart/add response AND THEN sit on a 100ms timer before opening — so the
       drawer appeared at (network round trip + 100ms), and its contents only after
       a SECOND round trip for the section. On a phone that is comfortably half a
       second of nothing happening after a deliberate tap, which reads as a dead
       button.

       Nothing about opening the drawer needs the server. Open it the instant the
       request leaves, and let refreshCartSection() fill it in when the response
       lands. The perceived cost goes to roughly zero and the real work is unchanged.
       If the add fails, the refresh still runs and the drawer simply shows the cart
       as it really is. */
    /* ASK FOR THE DRAWER IN THE REQUEST THAT IS ALREADY LEAVING.
       Tracy 2026-09-27: "I want it to actually add instantly in the HTML rather
       than a placeholder. How do we do it in less time?"

       Shopify's AJAX API renders sections INTO the add response when the request
       carries `sections`. Dawn's product-form.js already uses this (lines 36-40) —
       it just asks for the cart PAGE's sections (main-cart-items, cart-icon-bubble)
       and never for ours, so the drawer went and fetched itself a second time.

       Measured on the live AU store:
         add, then GET /cart?section_id   1.288s + 0.524s = 1.813s   (two trips)
         add WITH sections=quick-cart     1.141s                     (one trip)
       The section render is effectively free once it rides along, and the response
       carries the real Liquid-rendered line — no markup duplicated into JS, which
       is the thing that makes a hand-built optimistic row rot.

       Both body shapes are handled: Dawn sends FormData, our own callers send JSON
       strings. Anything unrecognised is left untouched and simply falls back to the
       old second fetch, so a future caller cannot be broken by this. */
    function requestQuickCartSection(args) {
      try {
        var init = args[1];
        if (!init || !init.body) return;
        var want = getSectionId();

        if (typeof FormData !== 'undefined' && init.body instanceof FormData) {
          var cur = init.body.get('sections');
          if (!cur) init.body.set('sections', want);
          else if (String(cur).indexOf(want) === -1) init.body.set('sections', cur + ',' + want);
          if (!init.body.get('sections_url')) init.body.set('sections_url', window.location.pathname);
          return;
        }

        if (typeof init.body === 'string' && init.body.charAt(0) === '{') {
          var o = JSON.parse(init.body);
          var s = o.sections;
          if (!s) o.sections = want;
          else if (String(s).indexOf(want) === -1) o.sections = String(s) + ',' + want;
          if (!o.sections_url) o.sections_url = window.location.pathname;
          init.body = JSON.stringify(o);
        }
      } catch (e) { /* leave the request exactly as it was */ }
    }

    /* Apply the section HTML the add response already carries. Returns true if it
       painted, so the caller knows it does not need the second round trip. */
    function applySectionsFromAddResponse(data) {
      try {
        var secs = data && data.sections;
        if (!secs) return false;
        var html = secs[getSectionId()] || secs['quick-cart'];
        if (!html) return false;

        var temp = document.createElement('div');
        temp.innerHTML = html;
        var fresh = temp.querySelector(QUICK_CART_SELECTOR);
        var current = document.querySelector(QUICK_CART_SELECTOR);
        if (!fresh || !current) return false;

        /* Same node-moving path as refreshCartSection, so images that are already
           painted are kept and the swap is invisible. */
        carryOverDecodedImages(current, fresh);
        if (current.replaceChildren) {
          current.replaceChildren.apply(current, Array.prototype.slice.call(fresh.childNodes));
        } else {
          current.innerHTML = fresh.innerHTML;
        }
        if (isDrawerOpen()) {
          var wrapper = document.querySelector('.quick-cart__wrapper');
          if (wrapper) {
            wrapper.classList.add('active');
            wrapper.setAttribute('aria-hidden', 'false');
          }
        }
        updateCartCount();
        /* This response IS the newest truth — make sure an older in-flight refresh
           cannot land on top of it. */
        refreshGen++;
        return true;
      } catch (e) {
        return false;
      }
    }

    var _origFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      var isAdd = url.indexOf('/cart/add') !== -1;

      if (isAdd) {
        requestQuickCartSection(args);
        suppressDawnNotification();
        openDrawer({ auto: true, skipRefresh: true });  /* synchronous, before the network */
        showAddingState();
      }

      var result = _origFetch.apply(this, args);

      if (isAdd) {
        /* Refresh on EVERY outcome, not just r.ok. The comment above always claimed
           "if the add fails, the refresh still runs and the drawer simply shows the
           cart as it really is" — but the code only refreshed on ok, so a rejected
           add (sold out, quantity unavailable) left the drawer showing whatever was
           there before, plus, now, a skeleton row that would never resolve and an
           armed countdown that would never start. Making the code match what it
           said costs one refresh on a rare path and removes a stuck state. */
        result.then(function (r) {
          if (r && r.ok) suppressDawnNotification();
          /* Read the body without consuming the caller's copy — Dawn reads this
             same response after us, and a used-up stream would break its own
             handling. clone() is the whole reason this is safe to do here. */
          var probe = null;
          try { probe = r && r.ok ? r.clone().json() : null; } catch (e) { probe = null; }

          if (!probe) { refreshCartSection(); return; }

          probe.then(function (data) {
            /* One round trip: the section HTML rode back with the add. */
            if (!applySectionsFromAddResponse(data)) refreshCartSection();
            startArmedDismiss();
          }).catch(function () { refreshCartSection(); });
        }).catch(function () {
          refreshCartSection();
        });
      }
      return result;
    };
  }

  /* WHILE AN ADD IS IN FLIGHT, SAY SO (BBB panel review, Tracy 2026-10-08). The drawer opens the instant
     the add leaves (above), so with an empty bag it showed "Nothing here yet" for about a second before the
     item arrived: the first thing a shopper saw after Add to bag was an empty bag. Until the drawer's
     contents are replaced (the add's own section HTML, or the refresh), the empty state is swapped for
     "Adding to your bag...". Replacing the contents removes the note with them, so it can never stick; a 4 s
     fallback puts the empty state back if no response arrives. */
  function showAddingState() {
    var quickCart = document.querySelector(QUICK_CART_SELECTOR);
    var empty = quickCart && quickCart.querySelector('.cnc-empty');
    if (!empty || quickCart.querySelector('.cnc-adding')) return;
    var note = document.createElement('div');
    note.className = 'cnc-adding';
    note.setAttribute('role', 'status');
    note.textContent = 'Adding to your bag…';
    note.style.cssText = 'padding:56px 24px;text-align:center;font-size:17px;opacity:.85';
    empty.style.display = 'none';
    empty.parentNode.insertBefore(note, empty);
    setTimeout(function () { if (note.isConnected) { note.remove(); empty.style.display = ''; } }, 4000);
  }

  /* ============================================================
     INIT
     ============================================================ */
  function init() {
    bindCartOperations();
    bindDawnATC();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
